import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Search, Plus, Minus, X, Package } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { searchModels, matchRank } from './modelSearch';
import type { DeviceModelRow } from '../../services/hardwareApi';

/**
 * SEARCH-FIRST GRN LINE COMPOSER.
 *
 * Replaces a per-line `SearchableDropdown` that showed text only. Adding six
 * devices used to mean six open-scroll-select cycles against model names, with
 * nothing on screen to tell a P17 from a QD4. Here one box stays focused, the
 * results carry the device's photo, and Enter adds.
 *
 * The quantity rule is the scanner's: adding a model that is already in the
 * basket INCREMENTS it. A scanner firing one barcode five times leaves qty 5,
 * which is the same behaviour the rest of the scanner-first flows use.
 */

export interface GrnLineDraft {
  modelId: string;
  qty: number;
  unitCostCents: number;
}

const MAX_RESULTS = 6;

const isQty = (m: Pick<DeviceModelRow, 'trackingMode'>) => m.trackingMode === 'quantity';

/** Highlights the matched run so the operator sees WHY a row came back. */
const Marked: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  const needle = q.trim().toLowerCase();
  const at = needle ? text.toLowerCase().indexOf(needle) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-sm bg-amber-200 px-0.5 text-inherit">{text.slice(at, at + needle.length)}</mark>
      {text.slice(at + needle.length)}
    </>
  );
};

const Thumb: React.FC<{ model?: DeviceModelRow; size?: number }> = ({ model, size = 44 }) => (
  <ShimmerImage
    src={model?.media?.heroImageUrl}
    alt={model?.modelName || 'Device'}
    width={size}
    height={size}
    objectFit="contain"
    variant="light"
    className="shrink-0 rounded-lg border border-stone-200 bg-stone-50"
    fallbackBgColor="bg-stone-50"
    fallbackIcon={<Package size={Math.round(size * 0.45)} className="text-stone-300" />}
  />
);

const GrnLineComposer: React.FC<{
  models: DeviceModelRow[];
  lines: GrnLineDraft[];
  onChange: (lines: GrnLineDraft[]) => void;
  /** Opens the shared DeviceQuickView. Omitted = rows are not clickable. */
  onInspect?: (modelId: string) => void;
}> = ({ models, lines, onChange, onInspect }) => {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const modelById = useMemo(() => new Map(models.map((m) => [m.modelId, m])), [models]);
  const hits = useMemo(() => searchModels(models, q).slice(0, MAX_RESULTS), [models, q]);

  useEffect(() => { if (sel >= hits.length) setSel(Math.max(0, hits.length - 1)); }, [hits.length, sel]);

  const add = (model: DeviceModelRow) => {
    const existing = lines.find((l) => l.modelId === model.modelId);
    onChange(existing
      ? lines.map((l) => (l.modelId === model.modelId ? { ...l, qty: l.qty + 1 } : l))
      : [...lines, {
        modelId: model.modelId,
        qty: 1,
        // Seeded from the model's own cost so the common case needs no typing;
        // still editable per line, because a shipment can be priced differently.
        unitCostCents: model.economics?.unitCostCents ?? 0,
      }]);
    setFlash(model.modelId);
    setQ('');
    setSel(0);
    window.setTimeout(() => setFlash(null), 520);
    inputRef.current?.focus();
  };

  /**
   * A scanner types the whole code then sends Enter. `matchRank === 0` is a
   * WHOLE-barcode match and nothing else, so this can never fire on a partial
   * read — a half-scan falls through to the normal picker instead of silently
   * receiving stock against the wrong device.
   */
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const exact = hits.find((m) => matchRank(m, q) === 0);
    const pick = exact || hits[sel];
    if (pick) add(pick);
  };

  const setQty = (modelId: string, qty: number) =>
    onChange(lines.map((l) => (l.modelId === modelId ? { ...l, qty: Math.max(1, qty) } : l)));
  const setCost = (modelId: string, cents: number) =>
    onChange(lines.map((l) => (l.modelId === modelId ? { ...l, unitCostCents: Math.max(0, cents) } : l)));
  const remove = (modelId: string) => onChange(lines.filter((l) => l.modelId !== modelId));

  const units = lines.reduce((s, l) => s + l.qty, 0);
  const grandCents = lines.reduce((s, l) => s + l.qty * l.unitCostCents, 0);
  const money = (c: number) => `$${(c / 100).toFixed(2)}`;

  return (
    <div>
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className="text-xs font-bold text-stone-800">Add devices</h3>
        <span className="text-[11px] text-stone-500">
          Type a name, brand or barcode · <b>Enter</b> adds · scan the same code twice for qty 2
        </span>
      </div>

      <div className="flex min-h-[48px] items-center gap-2 rounded-lg border-2 border-[#DC143C] bg-white px-3">
        <Search size={17} className="shrink-0 text-[#DC143C]" />
        <input
          ref={inputRef}
          value={q}
          autoFocus
          onChange={(e) => { setQ(e.target.value); setSel(0); }}
          onKeyDown={onKeyDown}
          placeholder="Search or scan a device…"
          aria-label="Search devices to add"
          className="flex-1 border-0 bg-transparent py-2.5 text-sm outline-none placeholder:text-stone-400"
        />
        <kbd className="hidden rounded border border-stone-300 border-b-2 bg-stone-50 px-1.5 py-0.5 font-mono text-[9.5px] text-stone-500 sm:inline">↑↓ move</kbd>
        <kbd className="hidden rounded border border-stone-300 border-b-2 bg-stone-50 px-1.5 py-0.5 font-mono text-[9.5px] text-stone-500 sm:inline">↵ add</kbd>
      </div>

      <div className="mt-1.5 overflow-hidden rounded-lg border border-stone-200 bg-white">
        {hits.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-stone-500">
            Nothing matches <b className="text-stone-800">{q}</b>. Check the barcode, or search by brand.
          </p>
        ) : hits.map((m, i) => (
          <button
            key={m.modelId}
            type="button"
            onClick={() => add(m)}
            onMouseEnter={() => setSel(i)}
            className={`grid w-full grid-cols-[44px_1fr_auto] items-center gap-3 border-b border-stone-100 px-3 py-2 text-left last:border-b-0 ${
              i === sel ? 'bg-[#FFF1F3] shadow-[inset_3px_0_0_#DC143C]' : 'bg-white hover:bg-stone-50'
            }`}
          >
            <Thumb model={m} />
            <span className="min-w-0">
              <span className="block truncate text-[13.5px] font-semibold text-stone-900">
                <Marked text={m.modelName} q={q} />
              </span>
              <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11.5px] text-stone-500">
                <span className="truncate">
                  <Marked text={m.manufacturer} q={q} />{m.brand ? <> · <Marked text={m.brand} q={q} /></> : null}
                </span>
                <span className={`rounded-full border px-1.5 py-px text-[9.5px] font-bold uppercase tracking-wide ${
                  isQty(m) ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-stone-300 bg-stone-100 text-stone-700'
                }`}>{isQty(m) ? 'Total' : 'Individual'}</span>
                {m.sharedBarcode && <span className="font-mono text-[10.5px]"><Marked text={m.sharedBarcode} q={q} /></span>}
              </span>
            </span>
            {onInspect && (
              <span
                role="button"
                tabIndex={0}
                aria-label={`Details for ${m.modelName}`}
                onClick={(e) => { e.stopPropagation(); onInspect(m.modelId); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onInspect(m.modelId); } }}
                className="rounded-md border border-stone-200 px-2 py-1 font-mono text-[10.5px] text-stone-500 hover:border-stone-300 hover:bg-stone-100"
              >
                Details
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ---- basket ---- */}
      <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
        <div className="grid grid-cols-[44px_1fr_128px_100px_88px_34px] gap-3 border-b border-stone-200 bg-stone-50 px-3 py-2 font-mono text-[9.5px] uppercase tracking-wider text-stone-500">
          <span /><span>Device</span><span>Quantity</span><span className="text-right">Unit cost</span><span className="text-right">Line total</span><span />
        </div>

        {lines.length === 0 ? (
          <p className="bg-white px-3 py-8 text-center text-[13px] text-stone-500">
            Nothing added yet — search above, or scan a barcode.
          </p>
        ) : lines.map((l) => {
          const m = modelById.get(l.modelId);
          return (
            <div
              key={l.modelId}
              className={`grid grid-cols-[44px_1fr_128px_100px_88px_34px] items-center gap-3 border-b border-stone-100 px-3 py-2 last:border-b-0 ${
                flash === l.modelId ? 'bg-[#FFF1F3]' : 'bg-white'
              }`}
            >
              <button type="button" onClick={() => onInspect?.(l.modelId)} disabled={!onInspect} className="disabled:cursor-default">
                <Thumb model={m} />
              </button>
              <span className="min-w-0">
                <span className="block truncate text-[13.5px] font-semibold text-stone-900">{m?.modelName || l.modelId}</span>
                <span className="mt-0.5 block truncate text-[11.5px] text-stone-500">
                  {m?.manufacturer}{m?.sharedBarcode ? <span className="ml-2 font-mono text-[10.5px]">{m.sharedBarcode}</span> : null}
                </span>
              </span>

              <span className="inline-flex items-center overflow-hidden rounded-lg border border-stone-300 bg-white">
                <button type="button" aria-label="Decrease" onClick={() => setQty(l.modelId, l.qty - 1)}
                  className="h-11 w-8 bg-stone-50 text-stone-700 hover:bg-stone-200"><Minus size={13} className="mx-auto" /></button>
                <input
                  value={l.qty}
                  inputMode="numeric"
                  aria-label={`Quantity for ${m?.modelName || l.modelId}`}
                  onChange={(e) => setQty(l.modelId, parseInt(e.target.value.replace(/\D/g, '') || '1', 10))}
                  className="h-11 w-12 border-x border-stone-200 text-center font-mono text-[13px] outline-none"
                />
                <button type="button" aria-label="Increase" onClick={() => setQty(l.modelId, l.qty + 1)}
                  className="h-11 w-8 bg-stone-50 text-stone-700 hover:bg-stone-200"><Plus size={13} className="mx-auto" /></button>
              </span>

              <input
                value={(l.unitCostCents / 100).toFixed(2)}
                inputMode="decimal"
                aria-label={`Unit cost for ${m?.modelName || l.modelId}`}
                onChange={(e) => setCost(l.modelId, Math.round((parseFloat(e.target.value.replace(/[^\d.]/g, '')) || 0) * 100))}
                className="h-11 w-full rounded-lg border border-stone-300 px-2 text-right font-mono text-[12.5px] outline-none"
              />
              <span className="text-right font-mono text-[13px] font-semibold">{money(l.qty * l.unitCostCents)}</span>
              <button type="button" aria-label={`Remove ${m?.modelName || l.modelId}`} onClick={() => remove(l.modelId)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-stone-200 text-stone-400 hover:border-red-200 hover:bg-[#FFF1F3] hover:text-[#DC143C]">
                <X size={14} />
              </button>
            </div>
          );
        })}

        <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-3 py-2.5">
          <span className="text-xs text-stone-500">
            {lines.length
              ? `${lines.length} device${lines.length > 1 ? 's' : ''} · ${units} unit${units > 1 ? 's' : ''}`
              : 'No devices yet'}
          </span>
          <span className="font-mono text-[15px] font-bold">{money(grandCents)}</span>
        </div>
      </div>
    </div>
  );
};

export default GrnLineComposer;
